import { Injectable, signal, inject } from '@angular/core';
import { supabase } from '../supabase';
import { Municipality, MunicipalityIpMaskInput, MunicipalityIpMaskView } from '../models';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { isValidProxyEndpoint } from '../utils/mask.util';

@Injectable({
  providedIn: 'root'
})
export class MunicipalityService {
  public municipalitiesTable = signal<Municipality[]>([]);

  public currentMunicipalityId = signal<string | null>(null);

  private loadingService = inject(LoadingService);
  private toast = inject(ToastService);

  constructor() { }

  async refreshMunicipalities(): Promise<Municipality[]> {
    const { data, error } = await supabase
      .from('municipalities')
      .select('*')
      .order('name');

    if (error) throw error;

    const mapped = (data || []).map(m => ({
      id: m.id,
      name: m.name,
      isActive: m.is_active,
      logo_url: m.logo_url,
      enable_n8n: m.enable_n8n,
      n8n_webhook_url: m.n8n_webhook_url,
      enable_consultas: m.enable_consultas,
      enable_exames: m.enable_exames,
      enable_oci: m.enable_oci,
      enable_portal: m.enable_portal,
      enable_ubs_module: m.enable_ubs_module,
      ubs_distribution_mode: m.ubs_distribution_mode ?? 'CLASSIC',
      n8n_custom_payload: m.n8n_custom_payload
    }));

    this.municipalitiesTable.set(mapped);
    return mapped;
  }

  async getMunicipalities() {
    return this.refreshMunicipalities();
  }

  /**
   * @deprecated Geração automática 'MA-XXX' removida conforme solicitação.
   */
  async generateNextMunicipalityId(): Promise<string> {
    return "";
  }

  async addMunicipality(id: string, name: string, logo_url?: string, extras?: { enable_n8n?: boolean, n8n_webhook_url?: string, n8n_custom_payload?: string, enable_ubs_module?: boolean, ubs_distribution_mode?: 'CLASSIC' | 'ACS', enable_consultas?: boolean, enable_exames?: boolean, enable_oci?: boolean, enable_portal?: boolean }) {
    const { data, error } = await supabase
      .from('municipalities')
      .insert({
        id,
        name,
        is_active: true,
        logo_url,
        enable_n8n: extras?.enable_n8n ?? false,
        n8n_webhook_url: extras?.n8n_webhook_url,
        enable_ubs_module: extras?.enable_ubs_module ?? false,
        ubs_distribution_mode: extras?.ubs_distribution_mode ?? 'CLASSIC',
        enable_consultas: extras?.enable_consultas ?? false,
        enable_exames: extras?.enable_exames ?? false,
        enable_oci: extras?.enable_oci ?? false,
        enable_portal: extras?.enable_portal ?? true,
        n8n_custom_payload: extras?.n8n_custom_payload
      })
      .select().single();

    if (error) throw error;
    return data;
  }

  async updateMunicipality(id: string, updates: { name?: string, logo_url?: string, enable_n8n?: boolean, n8n_webhook_url?: string, n8n_custom_payload?: string, enable_ubs_module?: boolean, ubs_distribution_mode?: 'CLASSIC' | 'ACS', enable_consultas?: boolean, enable_exames?: boolean, enable_oci?: boolean, enable_portal?: boolean }) {
    const { error } = await supabase
      .from('municipalities')
      .update(updates)
      .eq('id', id);
    if (error) throw error;
  }

  async updateMunicipalityStatus(id: string, isActive: boolean) {
    const { error } = await supabase
      .from('municipalities')
      .update({ is_active: isActive })
      .eq('id', id);
    if (error) throw error;
  }

  async getMunicipalityIpMask(municipalityId: string): Promise<MunicipalityIpMaskView> {
    const defaultView: MunicipalityIpMaskView = {
      municipality_id: municipalityId,
      enabled: false,
      protocol: 'http',
      host: '',
      port: 8080,
      username: '',
      has_password: false,
      last_verified_exit_ip: null,
      last_verified_at: null,
      updated_at: null
    };

    if (!municipalityId) return defaultView;

    const { data, error } = await supabase.rpc('secure_get_municipality_ip_mask', {
      p_municipality_id: municipalityId
    });

    if (error) {
      console.warn('[MunicipalityService] Aviso ao consultar máscara de IP (verifique migração SQL):', error.message);
      return defaultView;
    }

    return {
      ...defaultView,
      ...(data as Partial<MunicipalityIpMaskView>)
    };
  }

  async saveMunicipalityIpMask(municipalityId: string, mask: MunicipalityIpMaskInput): Promise<MunicipalityIpMaskView> {
    if (!municipalityId) {
      throw new Error('Código do município inválido para salvar a Máscara de IP.');
    }

    if (mask.enabled) {
      const check = isValidProxyEndpoint(mask.host, mask.port);
      if (!check.valid) {
        throw new Error(check.reason || 'Configuração de proxy inválida.');
      }
    }

    const { data, error } = await supabase.rpc('secure_upsert_municipality_ip_mask', {
      p_municipality_id: municipalityId,
      p_enabled: Boolean(mask.enabled),
      p_protocol: mask.protocol || 'http',
      p_host: (mask.host || '').trim(),
      p_port: Number(mask.port || 8080),
      p_username: mask.username ? mask.username.trim() : null,
      p_password: mask.password ? mask.password : null,
      p_clear_password: Boolean(mask.clear_password)
    });

    if (error) throw error;
    return data as MunicipalityIpMaskView;
  }

  async deleteMunicipality(id: string) {
    await this.prepareMunicipalityForDeletion(id);
    
    const { error } = await supabase
      .from('municipalities')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async prepareMunicipalityForDeletion(municipalityId: string) {
    if (!municipalityId) throw new Error("Município inválido");

    const tablesToClean = [
      'appointments',
      'patients', 
      'operators',
      'procedure_quotas',
      'system_logs',
      'quota_pools',
      'procedure_locations',
      'executors',
      'procedures',
      'acs',
      'acs_teams',
      'professionals',
      'professional_locations',
      'professional_teams',
      'professional_team_quotas',
      'municipality_ip_masks',
      'webhook_logs',
      'webhooks',
      'legal_documents',
      'patient_history',
      'exam_group_members',
      'procedure_imaging_locations',
      'queue_counters',
      'surgery_steps_config',
      'quota_distribution_templates',
      'ubs_daily_quotas'
    ];

    for (const table of tablesToClean) {
      try {
        const { error } = await supabase
          .from(table)
          .delete()
          .eq('municipality_id', municipalityId);
        
        if (error && error.code !== 'PGRST116') {
          console.warn(`[MunicipalityService] Tabela ${table} não encontrada ou erro:`, error.message);
        }
      } catch (e) {
        console.warn(`[MunicipalityService] Erro ao limpar ${table}:`, e);
      }
    }

    await this.clearAllPatients(municipalityId);
  }

  async clearAllPatients(municipalityId: string) {
    if (!municipalityId) throw new Error("Município inválido");

    // 1. Reset agenda slots (free them)
    const { error: slotError } = await supabase
      .from('agenda_slots')
      .update({
        patient_id: null,
        appointment_id: null,
        is_available: true,
        start_time: null,
        end_time: null,
        location_name: null,
        professional_name: null
      })
      .eq('municipality_id', municipalityId);

    if (slotError) console.error('[MunicipalityService] Erro ao liberar vagas:', slotError);

    // 2. Zerar booked_count dos pools
    const { error: poolResetError } = await supabase
      .from('quota_pools')
      .update({ booked_count: 0, updated_at: new Date().toISOString() })
      .eq('municipality_id', municipalityId);

    if (poolResetError) console.error('[MunicipalityService] Erro ao resetar booked_count dos pools:', poolResetError);

    // 3. Delete fiscal infrastructure
    await supabase.from('historico_auditoria').delete().eq('operador_id', 0);
    await supabase.from('protocolos_fiscais').delete().eq('municipio_id', municipalityId);

    // 4. Delete appointments
    const { error: appoError } = await supabase
      .from('appointments')
      .delete()
      .eq('municipality_id', municipalityId);

    if (appoError) throw appoError;

    // 5. Delete patients
    const { error: patientError } = await supabase
      .from('patients')
      .delete()
      .eq('municipality_id', municipalityId);

    if (patientError) throw patientError;
  }

  /**
   * Duplica um município existente e todas as suas configurações
   * (Locais, Executores, Procedimentos, Etapas de Cirurgia) de forma independente.
   */
  async duplicateMunicipality(sourceId: string, newName: string) {
    try {
      this.loadingService.set(true);
      const newId = prompt('Digite o ID (IBGE ou manual) para o novo município:');
      if (!newId) {
        this.toast.show('ID é obrigatório para duplicação.', 'error');
        return;
      }

      console.log(`[MunicipalityService] Duplicando ${sourceId} para novo ID ${newId} (${newName})...`);

      // 1. Obter dados do município de origem
      const { data: sourceMun, error: sourceError } = await supabase
        .from('municipalities')
        .select('*')
        .eq('id', sourceId)
        .single();

      if (sourceError) throw sourceError;

      // 2. Criar novo município
      const { created_at: _, id: __, name: ___, ...munCloneData } = sourceMun;
      const { error: munInsertError } = await supabase
        .from('municipalities')
        .insert({ ...munCloneData, id: newId, name: newName });

      if (munInsertError) throw munInsertError;

      console.log(`[MunicipalityService] Município ${newId} criado. Iniciando clonagem de dependências...`);

      // 3. Clonar Locais (procedure_locations)
      const { data: locs, error: locError } = await supabase
        .from('procedure_locations')
        .select('*')
        .eq('municipality_id', sourceId);

      if (locError) throw locError;

      const locationMap = new Map<string, string>();
      if (locs && locs.length > 0) {
        for (const loc of locs) {
          const { id: oldId, created_at: _, ...lData } = loc;
          const { data: newLoc, error: lInsertError } = await supabase
            .from('procedure_locations')
            .insert({ ...lData, municipality_id: newId })
            .select()
            .single();

          if (lInsertError) throw lInsertError;
          locationMap.set(oldId, newLoc.id);
        }
      }

      // 4. Clonar Executores (executors)
      const { data: execs, error: execError } = await supabase
        .from('executors')
        .select('*')
        .eq('municipality_id', sourceId);

      if (execError) throw execError;

      if (execs && execs.length > 0) {
        const execClones = execs.map(e => {
          const { id: _, created_at: __, ...eData } = e;
          return { ...eData, municipality_id: newId };
        });
        const { error: execInsertError } = await supabase.from('executors').insert(execClones);
        if (execInsertError) throw execInsertError;
      }

      // 5. Clonar Procedimentos (procedures) e Configurações de Cirurgia
      const { data: procs, error: procError } = await supabase
        .from('procedures')
        .select('*, surgery_steps_config: surgery_steps_config!parent_procedure_id(*)')
        .eq('municipality_id', sourceId);

      if (procError) throw procError;

      if (procs && procs.length > 0) {
        const procedureIdMap = new Map<number, number>();
        const allOriginalSteps: any[] = [];

        // 5a. Primeiro passo: Inserir todos os procedimentos para obter os novos IDs
        for (const p of procs) {
          const { id: oldId, created_at: _, surgery_steps_config: steps, ...pData } = p;
          const { data: newProc, error: pInsertError } = await supabase
            .from('procedures')
            .insert({ ...pData, municipality_id: newId, audit_trail: [] })
            .select()
            .single();

          if (pInsertError) throw pInsertError;
          procedureIdMap.set(oldId, newProc.id);

          if (steps && steps.length > 0) {
            allOriginalSteps.push({ oldParentId: oldId, newParentId: newProc.id, steps });
          }
        }

        // 5b. Segundo passo: Clonar as etapas de cirurgia (Surgery Steps)
        const stepsToInsert: any[] = [];
        for (const group of allOriginalSteps) {
          for (const s of group.steps) {
            const newStepProcId = procedureIdMap.get(s.step_procedure_id);
            const finalStepProcId = newStepProcId || s.step_procedure_id;

            stepsToInsert.push({
              parent_procedure_id: group.newParentId,
              step_procedure_id: finalStepProcId,
              step_order: s.step_order
            });
          }
        }

        if (stepsToInsert.length > 0) {
          const { error: stepsError } = await supabase.from('surgery_steps_config').insert(stepsToInsert);
          if (stepsError) throw stepsError;
        }
      }

      this.toast.show('Município duplicado com sucesso!', 'success');
      return newId;
    } catch (err) {
      console.error('[MunicipalityService] Erro crítico ao duplicar município:', err);
      this.toast.show('Erro ao duplicar município.', 'error');
      throw err;
    } finally {
      this.loadingService.set(false);
    }
  }
}

